import { useQuery } from '@apollo/client';
import { Card, Table } from 'antd';
import { useModal } from 'modules/common/hooks';
import { IDonorRecord } from 'modules/dashboard/types/donor';
import { FC, useMemo, useRef } from 'react';

import { getDateRangeBounds } from '../../../../contexts/PhlebotomistDashboardContext/usePhlebotomistStatsData';
import { GET_RECRUITER_DONORS_QUERY } from '../../../../graphql/queries';
import { DateRangeType } from '../../../../types/phlebotomist-dashboard';
import {
  DEFAULT_DATE_RANGE_FALLBACK,
  VALID_DATE_RANGES,
} from '../../constants';
import { buildColumns } from '../../donorColumns/buildColumns';
import { buildCsvColumns } from '../../donorColumns/buildCsvColumns';
import {
  DEFAULT_VISIBLE_COLUMNS,
  COLUMN_VISIBILITY_LABELS,
} from '../../donorColumns/columnRegistry';
import { PRBC_DONOR_KEYS } from '../../donorColumns/tableColumnKeys';
import { useColumnVisibility } from '../../hooks/useColumnVisibility';
import { useUrlParams } from '../../hooks/useUrlParams';
import { ColumnKey, ITabViewProps } from '../../types';
import { downloadCsv, makeCsvFilename } from '../../utils/csvUtils';
import ColumnVisibilityPanel from '../ColumnVisibilityPanel';
import DonorDetailsHeader from '../DonorDetailsHeader';
import { DonorDonationHistoryModal } from '../DonorDonationHistoryModal';
import TableToolbar from '../TableToolbar';

interface IRecruiterDonorsViewProps extends ITabViewProps {
  recruiterId: string | null;
  dateRange: DateRangeType | undefined;
}

export const RecruiterDonorsView: FC<IRecruiterDonorsViewProps> = ({
  recruiterId,
  dateRange,
  meta,
  onBack,
  onLoadActiveDonors,
}) => {
  const {
    table: { colVisKey },
  } = useUrlParams();
  const {
    config: drilldownConfig,
    show: openDrilldown,
    hide: closeDrilldown,
  } = useModal<{ donor: IDonorRecord | null }>({ donor: null });
  const filteredRef = useRef<IDonorRecord[] | null>(null);

  const { visibleColumns, handleColumnVisibilityChange } = useColumnVisibility(
    colVisKey,
    DEFAULT_VISIBLE_COLUMNS
  );

  const dateBounds = useMemo(() => {
    const validDr =
      dateRange && VALID_DATE_RANGES.has(dateRange)
        ? dateRange
        : DEFAULT_DATE_RANGE_FALLBACK;
    return getDateRangeBounds(validDr);
  }, [dateRange]);

  const { data, loading } = useQuery(GET_RECRUITER_DONORS_QUERY, {
    variables: {
      input: {
        recruiterId: recruiterId || '',
        start: dateBounds.start.toISOString(),
        end: dateBounds.end.toISOString(),
      },
    },
    skip: !recruiterId,
  });

  const donors: IDonorRecord[] = useMemo(
    () => data?.getRecruiterDonors?.donors ?? [],
    [data]
  );
  const total: number = data?.getRecruiterDonors?.total ?? 0;

  const columns = useMemo(
    () =>
      buildColumns(PRBC_DONOR_KEYS, { donors }).filter((col) => {
        const k = col.key as ColumnKey;
        return !(k in visibleColumns) || visibleColumns[k];
      }),
    [donors, visibleColumns]
  );

  const handleExport = () => {
    const csvCols = buildCsvColumns(
      PRBC_DONOR_KEYS,
      { donors },
      { isRetired: false, isPending: false, visibleColumns }
    );
    const exportData = filteredRef.current ?? donors;
    downloadCsv(
      csvCols.map((c) => c.label),
      exportData.map((d) => csvCols.map((c) => c.value(d))),
      makeCsvFilename('recruiter-donors')
    );
  };

  return (
    <div style={{ padding: 0 }}>
      <DonorDetailsHeader
        title={meta.title}
        description={meta.description}
        iconColor={meta.iconColor}
        donorCount={total}
        loading={loading}
        onBack={onBack}
        onLoadActiveDonors={onLoadActiveDonors}
      />
      <Card bodyStyle={{ padding: '16px 20px' }}>
        <TableToolbar
          iconColor={meta.iconColor}
          title="Donor List"
          exportDisabled={!donors.length}
          onExport={handleExport}
        />

        <ColumnVisibilityPanel
          columns={COLUMN_VISIBILITY_LABELS}
          visibleColumns={visibleColumns}
          onChange={handleColumnVisibilityChange}
        />

        <Table
          rowKey="donorId"
          columns={columns}
          dataSource={donors}
          loading={loading}
          scroll={{ x: 1000 }}
          pagination={{
            defaultPageSize: 100,
            pageSizeOptions: ['10', '20', '50', '100'],
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} donors`,
          }}
          onChange={(_p, _f, _s, extra) => {
            filteredRef.current = extra.currentDataSource as IDonorRecord[];
          }}
          onRow={(record) => ({
            onClick: (e) => {
              if ((e.target as HTMLElement).closest('a, button, input')) return;
              openDrilldown({ donor: record });
            },
            style: { cursor: 'pointer' },
          })}
          size="small"
          style={{ marginTop: 8 }}
        />
      </Card>

      <DonorDonationHistoryModal
        donor={drilldownConfig.donor}
        visible={drilldownConfig.visible ?? false}
        onClose={closeDrilldown}
      />
    </div>
  );
};

export default RecruiterDonorsView;
